import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StorefrontLayout } from '../../components/storefront/StorefrontLayout';
import { DeadlineBanner } from '../../components/storefront/DeadlineBanner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { mockStores } from '../../utils/mockData';
import { mockProducts } from '../../utils/mockProducts';
import { calculateStoreStatus } from '../../utils/storeStatus';
import { useCart } from '../../hooks/useCart';
import { ArrowLeft, Check } from 'lucide-react';
export function ProductDetail() {
  const {
    slug,
    productId
  } = useParams<{
    slug: string;
    productId: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const store = mockStores.find(s => s.slug === slug);
  const product = mockProducts.find(p => p.id === productId);
  const [selectedVariantId, setSelectedVariantId] = useState(product?.variants[0]?.id || '');
  const [personalization, setPersonalization] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  if (!store || !product) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-1332">
        Product not found
      </div>;
  }
  const status = calculateStoreStatus(store);
  const isOpen = status === 'open' || status === 'closing-soon';
  const selectedVariant = product.variants.find(v => v.id === selectedVariantId);
  const validatePersonalization = () => {
    const newErrors: Record<string, string> = {};
    product.personalizationFields.forEach(field => {
      const value = personalization[field.id] || '';
      if (field.required && !value.trim()) {
        newErrors[field.id] = `${field.label} is required`;
      }
      if (value && field.characterLimit && value.length > field.characterLimit) {
        newErrors[field.id] = `Maximum ${field.characterLimit} characters`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleAddToCart = () => {
    if (!validatePersonalization() || !selectedVariant) return;
    addToCart({
      productId: product.id,
      productName: product.name,
      variantId: selectedVariant.id,
      variantName: selectedVariant.name,
      price: selectedVariant.price,
      quantity: 1,
      personalization: Object.keys(personalization).length > 0 ? personalization : undefined
    });
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  return <StorefrontLayout storeName={store.name} storeSlug={store.slug} organizationName={store.organizationName} supportEmail={store.supportEmail} supportPhone={store.supportPhone} data-id="element-1333">
      {isOpen && <DeadlineBanner closeDate={store.settings.closeDate} isClosingSoon={status === 'closing-soon'} data-id="element-1334" />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1335">
        <button onClick={() => navigate(`/store/${slug}`)} className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors" data-id="element-1336">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-1337" />
          Back to Store
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1338">
          {/* Product Image */}
          <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-1339">
            <div className="text-gray-400" data-id="element-1340">Product Image</div>
          </div>

          {/* Product Info */}
          <div className="space-y-6" data-id="element-1341">
            <div data-id="element-1342">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1343">
                {product.name}
              </h1>
              <p className="text-gray-600" data-id="element-1344">{product.description}</p>
            </div>

            <div className="border-t border-gray-200 pt-6" data-id="element-1345">
              <p className="text-3xl font-bold text-gray-900" data-id="element-1346">
                ${selectedVariant?.price.toFixed(2)}
              </p>
            </div>

            {/* Variant Selection */}
            <div data-id="element-1347">
              <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-1348">
                Select Size & Color
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-1349">
                {product.variants.map(variant => <button key={variant.id} onClick={() => setSelectedVariantId(variant.id)} className={`
                      px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all
                      ${selectedVariantId === variant.id ? 'border-red-600 bg-red-50 text-red-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}
                    `} data-id="element-1350">
                    {variant.name}
                  </button>)}
              </div>
            </div>

            {/* Personalization Fields */}
            {product.personalizationFields.length > 0 && <div className="border-t border-gray-200 pt-6" data-id="element-1351">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1352">
                  Personalization
                </h3>
                <div className="space-y-4" data-id="element-1353">
                  {product.personalizationFields.map(field => <div key={field.id} data-id="element-1354">
                      <Input label={`${field.label}${field.required ? ' *' : ''}`} type={field.type === 'number' ? 'number' : 'text'} value={personalization[field.id] || ''} onChange={e => setPersonalization(prev => ({
                  ...prev,
                  [field.id]: e.target.value
                }))} error={errors[field.id]} helperText={field.characterLimit ? `Max ${field.characterLimit} characters` : undefined} maxLength={field.characterLimit} data-id="element-1355" />
                    </div>)}
                </div>
              </div>}

            {/* Add to Cart */}
            <div className="border-t border-gray-200 pt-6" data-id="element-1356">
              {isOpen ? <Button onClick={handleAddToCart} className="w-full" size="lg" data-id="element-1357">
                  Add to Cart
                </Button> : <Button disabled className="w-full opacity-50" size="lg" data-id="element-1358">
                  Store Not Open
                </Button>}

              {showSuccess && <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700" data-id="element-1359">
                  <Check className="h-5 w-5 mr-2" data-id="element-1360" />
                  <span className="text-sm font-medium" data-id="element-1361">Added to cart!</span>
                </div>}
            </div>

            {/* Product Details */}
            <div className="border-t border-gray-200 pt-6 text-sm text-gray-600 space-y-2" data-id="element-1362">
              <p data-id="element-1363">
                <strong data-id="element-1364">Decoration:</strong> {product.decorationMethod}
              </p>
              <p data-id="element-1365">
                <strong data-id="element-1366">Locations:</strong>{' '}
                {product.decorationLocations.join(', ')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </StorefrontLayout>;
}